import * as React from "react";
import { GoogleApiWrapper } from "google-maps-react";
import PlacesAutocomplete from "react-places-autocomplete";

const states = [
    ["AL", "Alabama"],
    ["AK", "Alaska"],
    ["AZ", "Arizona"],
    ["AR", "Arkansas"],
    ["CA", "California"],
    ["CO", "Colorado"],
    ["CT", "Connecticut"],
    ["DE", "Delaware"],
    ["DC", "District of Columbia"],
    ["FL", "Florida"],
    ["GA", "Georgia"],
    ["HI", "Hawaii"],
    ["ID", "Idaho"],
    ["IL", "Illinois"],
    ["IN", "Indiana"],
    ["IA", "Iowa"],
    ["KS", "Kansas"],
    ["KY", "Kentucky"],
    ["LA", "Louisiana"],
    ["ME", "Maine"],
    ["MD", "Maryland"],
    ["MA", "Massachusetts"],
    ["MI", "Michigan"],
    ["MN", "Minnesota"],
    ["MS", "Mississippi"],
    ["MO", "Missouri"],
    ["MT", "Montana"],
    ["NE", "Nebraska"],
    ["NV", "Nevada"],
    ["NH", "New Hampshire"],
    ["NJ", "New Jersey"],
    ["NM", "New Mexico"],
    ["NY", "New York"],
    ["NC", "North Carolina"],
    ["ND", "North Dakota"],
    ["OH", "Ohio"],
    ["OK", "Oklahoma"],
    ["OR", "Oregon"],
    ["PA", "Pennsylvania"],
    ["PR", "Puerto Rico"],
    ["RI", "Rhode Island"],
    ["SC", "South Carolina"],
    ["SD", "South Dakota"],
    ["TN", "Tennessee"],
    ["TX", "Texas"],
    ["UT", "Utah"],
    ["VT", "Vermont"],
    ["VA", "Virginia"],
    ["WA", "Washington"],
    ["WV", "West Virginia"],
    ["WI", "Wisconsin"],
    ["WY", "Wyoming"],
];

interface AddressComponents {
    street_address: string;
    street_address_2: string;
    city: string;
    state: string;
    zip: string;
}

interface AutocompleteAddressProps {
    google?: any;
    model?: string;
    street_address?: string;
    street_address_2?: string;
    city?: string;
    state?: string;
    zip?: string;
    setAddress?: (address: AddressComponents) => void;
}

interface GooglePlaceResult {
    address_components: Array<{
        long_name: string;
        short_name: string;
        types: string[];
    }>;
}

const separateComponents = (components: GooglePlaceResult, setState: React.Dispatch<React.SetStateAction<AddressComponents>>) => {
    let address_components: AddressComponents = {
        street_address: "",
        street_address_2: "",
        city: "",
        state: "",
        zip: "",
    };

    components.address_components.forEach(ac => {
        if (ac.types.includes("street_number")) {
            address_components["street_address"] = ac.long_name;
        }

        if (ac.types.includes("route")) {
            address_components["street_address"] = `${address_components["street_address"]} ${ac.long_name}`;
        }

        if (ac.types.includes("locality")) {
            address_components["city"] = ac.long_name;
        }

        if (ac.types.includes("administrative_area_level_1")) {
            // Use short_name for state abbreviation (e.g., "CA" instead of "California")
            address_components["state"] = ac.short_name;
        }

        if (ac.types.includes("postal_code")) {
            address_components["zip"] = ac.long_name;
        }
    });

    setState(state => ({ ...state, ...address_components }));
};

const placesLibraryLoaded = (): boolean =>
    typeof window !== "undefined" && !!(window as any).google?.maps?.places;

// PlacesAutocomplete throws during mount when the Google Maps script is not
// there yet or never arrives (blocked, slow network, billing lapse), which
// took down the whole profile form (Sentry DIRECTDENTAL-208). Until the
// library is present, render the same fields as plain inputs.
const SafePlacesAutocomplete = (props: React.ComponentProps<typeof PlacesAutocomplete>) => {
    if (placesLibraryLoaded()) {
        return <PlacesAutocomplete {...props} />;
    }
    return (props.children as any)({
        getInputProps: (options: Record<string, unknown> = {}) => ({
            ...options,
            value: props.value,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => props.onChange?.(e.target.value),
        }),
        suggestions: [],
        getSuggestionItemProps: (_suggestion: unknown, options: Record<string, unknown> = {}) => options,
        loading: false,
    });
};

const FormContainer: React.FC<AutocompleteAddressProps> = (props) => {
    const { google, model = "address" } = props;
    
    // Ensure proper state initialization - don't default to empty string if undefined
    const [state, setState] = React.useState<AddressComponents>({
        street_address: props.street_address || "",
        street_address_2: props.street_address_2 || "",
        city: props.city || "",
        state: props.state !== undefined ? props.state : "",
        zip: props.zip || "",
    });

    // Function to call props.setAddress if it exists
    const setAddress = () => {
        if (props.setAddress) {
            props.setAddress(state);
        }
    };

    const handleChange = (street_address: string) => {
        setState(prev => ({
            ...prev,
            street_address: street_address || ""
        }));
    };

    const handleUpdate = (name: keyof AddressComponents, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const value = e.target.value;

        if (name === "zip") {
            const zipIsValid = value.length <= 5 && !isNaN(Number(value));

            if (state.zip === undefined) {
                setState(state => ({ ...state, [name]: "" }));
            }

            if (value === "" || value === undefined || zipIsValid) {
                setState(state => ({ ...state, [name]: value }));
            }
        } else {
            setState(state => ({ ...state, [name]: value }));
        }
    };

    const handleError = () => {
        if (!Number(state.zip)) {
            return <p>Must be a 5 Digit U.S Zip Code</p>;
        }
    };

    const handleSelect = (street_address: string, placeId: string) => {
        setState(state => ({ ...state, street_address }));

        const request = { placeId: placeId };
        const placeRequest = new Promise<GooglePlaceResult>((resolve, reject) => {
            new google.maps.places.PlacesService(
                document.createElement("div")
            ).getDetails(request, (place: GooglePlaceResult, status: any) => {
                if (status === google.maps.places.PlacesServiceStatus.OK) {
                    resolve(place);
                } else {
                    reject();
                }
            });
        }).then(response => separateComponents(response, setState));
    };

    // Call setAddress after state is updated
    React.useEffect(() => {
        setAddress();
    }, [state]);

    return (
        <SafePlacesAutocomplete
            value={state.street_address}
            onChange={handleChange}
            onSelect={handleSelect}
            searchOptions={{ componentRestrictions: { country: ["us"] } }}>
            {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => {
                const hasSuggestions = suggestions.length > 0;

                return (
                    <div>
                        <div className="modern-form-group" style={{ position: 'relative' }}>
                            <label htmlFor="street_address">Street Address</label>
                            <input
                                {...getInputProps({ placeholder: "e.g. 123 Main Street", className: "search-field form-control" })}
                                name={`${model}[street_address]`}
                                id={`${model}_street_address`}
                            />

                            <div className={`autocomplete-dropdown-container ${hasSuggestions ? "modern-dropdown-suggestions" : ""}`}
                                style={hasSuggestions ? {
                                    position: 'absolute',
                                    zIndex: 1000,
                                    width: '100%',
                                    marginTop: '0.25rem',
                                    background: '#ffffff',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                    overflow: 'hidden'
                                } : {}}>
                                {loading && (
                                    <div style={{
                                        padding: '1rem',
                                        textAlign: 'center',
                                        color: '#6c757d',
                                        fontSize: '0.9rem'
                                    }}>
                                        <i className="fas fa-spinner fa-spin me-2"></i>
                                        Loading suggestions...
                                    </div>
                                )}
                                {suggestions.map((suggestion, index) => {
                                    const isActive = suggestion.active;
                                    const style = {
                                        padding: '0.75rem 1rem',
                                        cursor: 'pointer',
                                        fontSize: '0.95rem',
                                        borderBottom: index < suggestions.length - 1 ? '1px solid #f0f4f8' : 'none',
                                        background: isActive
                                            ? 'linear-gradient(135deg, rgba(8, 94, 200, 0.1) 0%, rgba(23, 111, 201, 0.1) 100%)'
                                            : '#ffffff',
                                        color: isActive ? '#085ec8' : '#2d3748',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center'
                                    };

                                    return (
                                        <div
                                            key={`${suggestion.placeId || suggestion.description}_${index}` || index}
                                            {...getSuggestionItemProps(suggestion, { style })}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(8, 94, 200, 0.1) 0%, rgba(23, 111, 201, 0.1) 100%)';
                                                e.currentTarget.style.color = '#085ec8';
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isActive) {
                                                    e.currentTarget.style.background = '#ffffff';
                                                    e.currentTarget.style.color = '#2d3748';
                                                }
                                            }}
                                        >
                                            <i className="fas fa-map-marker-alt me-2" style={{
                                                fontSize: '0.85rem',
                                                color: isActive ? '#085ec8' : '#9ca3af'
                                            }}></i>
                                            <span key={`span_${index}`}>{suggestion.description}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="modern-form-group">
                                <label htmlFor="street_address_2">Street Address 2</label>
                                <input
                                    id={`${model}_street_address_2`}
                                    className="search-field form-control"
                                    placeholder="e.g. Suite #100"
                                    type="text"
                                    value={state.street_address_2}
                                    name={`${model}[street_address_2]`}
                                    onChange={e => handleUpdate("street_address_2", e)}
                                />
                            </div>
                        </div>
                        <div className="modern-form-group">
                            <label htmlFor="city">City</label>
                            <input
                                id={`${model}_city`}
                                className="search-field form-control"
                                type="text"
                                placeholder="e.g. San Francisco"
                                value={state.city}
                                name={`${model}[city]`}
                                onChange={e => handleUpdate("city", e)}
                            />
                        </div>

                        <div className="row">
                            <StateField
                                selected={state.state}
                                model={model}
                                onChange={e => handleUpdate("state", e)}
                            />
                            <div className="col-lg-6">
                                <div className="modern-form-group">
                                    <label htmlFor="zip">Zip</label>
                                    <input
                                        id={`${model}_zip`}
                                        className="search-field form-control"
                                        required
                                        type="text"
                                        placeholder="e.g. 94102"
                                        value={state.zip}
                                        name={`${model}[zip]`}
                                        onChange={e => handleUpdate("zip", e)}
                                    />
                                    {handleError()}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            }}
        </SafePlacesAutocomplete>
    );
};

interface StateFieldProps {
    selected: string;
    model: string;
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

const StateField: React.FC<StateFieldProps> = ({ selected, model, onChange }) => {
    // Ensure we have a controlled component with a proper default value
    const currentValue = selected || "";
    
    return (
        <div className="col-lg-6">
            <div className="modern-form-group">
                <label htmlFor={`${model}_state`}>State</label>
                <select
                    id={`${model}_state`}
                className="form-select"
                name={`${model}[state]`}
                value={currentValue}
                onChange={onChange}
                required
            >
                <option value="">
                    Select State
                </option>
                {states.map(st => (
                    <option key={st[0]} value={st[0]}>
                        {st[1]}
                    </option>
                ))}
            </select>
            </div>
        </div>
    );
};

// Get API key from meta tag injected by Rails
const getGoogleMapsApiKey = (): string => {
    if (typeof document === 'undefined') return '';
    const metaTag = document.querySelector('meta[name="google-maps-api-key"]');
    return metaTag?.getAttribute('content') || '';
};

// Add setAddress props to FormContainer
export default GoogleApiWrapper({
    apiKey: getGoogleMapsApiKey(),
    libraries: ["places"],
})(FormContainer);
